import type {
  EpaApiTriFormREZ,
  EpaTriSection7A,
  WasteStreamCode,
  WasteStreamTreatmentCode,
} from '../../cross-app-shared/types/epa-api.ts';
import type { CasOrCategory } from '../types/db.ts';
import type {
  ChemicalActivitiesAndUses,
  QuestionnaireWasteTreatment,
  QuestionnaireWasteTreatmentContainer,
  WasteTreatmentID,
} from '../types/questionnaires.ts';
import { generateId } from '../../cross-app-shared/services/generate-id.ts';
import { epaFetch, epaIsTrueFlag } from '../../cross-app-shared/services/epa-api.ts';

export async function epaFetchSectionSevenA(
  ez: EpaApiTriFormREZ<CasOrCategory>,
  casOrCategory: CasOrCategory,
): Promise<QuestionnaireWasteTreatmentContainer> {
  if (!ez || ez.ons_wastestream_code_1 === null) {
    return { isApplicable: false, treatments: {} };
  }

  const response = (
    await epaFetch<EpaTriSection7A>({
      service: 'dmapservice',
      table: 'tri.rlps_onsite_waste_effcncy_mthd',
      filters: [
        {
          column: 'doc_ctrl_num',
          operator: 'equals',
          value: ez.doc_ctrl_num,
        },
      ],
    })
  ).sort((a, b) => {
    const streamA = Number(a.wastestream_seq_num);
    const streamB = Number(b.wastestream_seq_num);
    if (streamA !== streamB) {
      return streamA - streamB;
    }

    const treatmentA = Number(a.treatment_sequence);
    const treatmentB = Number(b.treatment_sequence);
    return treatmentA - treatmentB;
  });

  const bySequenceNum = new Map<string, EpaTriSection7A[]>();
  for (const line of response) {
    if (line.wastestream_code === 'N' || line.treatment_method_code === 'NA') {
      continue;
    }

    if (!bySequenceNum.has(line.wastestream_seq_num)) {
      bySequenceNum.set(line.wastestream_seq_num, []);
    }

    bySequenceNum.get(line.wastestream_seq_num)!.push(line);
  }

  const result: Partial<Record<WasteTreatmentID, QuestionnaireWasteTreatment>> = {};
  for (const lines of bySequenceNum.values()) {
    const treatment: QuestionnaireWasteTreatment = {
      wasteTreatmentID: generateId('waste_treatment'),
      chemicals: [casOrCategory],
      wasteStreamCode: lines[0].wastestream_code as WasteStreamCode,
      treatmentEfficiency: lines[0].efficiency_range_code ?? '',
      treatmentMethodSequence: lines.map((line) => line.treatment_method_code as WasteStreamTreatmentCode),
    };
    result[treatment.wasteTreatmentID] = treatment;
  }

  return { isApplicable: Object.keys(result).length > 0, treatments: result };
}

export function epaFormRToActivitiesAndUses(ez: EpaApiTriFormREZ<CasOrCategory>): ChemicalActivitiesAndUses {
  const reactantCategories: ChemicalActivitiesAndUses['process']['reactantCategories'] = [];
  const reactantMapping: Partial<
    Record<ChemicalActivitiesAndUses['process']['reactantCategories'][number], keyof EpaApiTriFormREZ<CasOrCategory>>
  > = {
    P101: 'reactant_feedstocks',
    P102: 'reactant_raw_materials',
    P103: 'reactant_intermediates',
    P104: 'reactant_initiators',
    // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
    // P105: 'reactant_catalyst',
    // P106: 'reactant_binder',
    P199: 'reactant_other',
  };
  for (const [category, key] of Object.entries(reactantMapping)) {
    if (epaIsTrueFlag(ez[key])) {
      reactantCategories.push(category as ChemicalActivitiesAndUses['process']['reactantCategories'][number]);
    }
  }

  const formulationComponents: ChemicalActivitiesAndUses['process']['formulationComponents'] = [];
  const formulationMapping: Partial<
    Record<ChemicalActivitiesAndUses['process']['formulationComponents'][number], keyof EpaApiTriFormREZ<CasOrCategory>>
  > = {
    P201: 'formul_comp_additives',
    P202: 'formul_comp_dyes',
    P203: 'formul_comp_reac_diluents',
    P204: 'formul_comp_initiators',
    P205: 'formul_comp_solvents',
    P206: 'formul_comp_inhibitors',
    P207: 'formul_comp_emulsifiers',
    P208: 'formul_comp_surfactants',
    P209: 'formul_comp_lubricants',
    P210: 'formul_comp_flame_retardants',
    P211: 'formul_comp_rheological_mods',
    // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
    // P212: 'formul_comp_binder',
    // P213: 'formul_comp_fuel_agents',
    // P214: 'formul_comp_deodorizer',
    // P215: 'formul_comp_fragrance',
    // P216: 'formul_comp_plasticizer',
    // P217: 'formul_comp_semiconductor',
    // P218: 'formul_comp_energy_releasers',
    // P219: 'formul_comp_foamant',
    // P220: 'formul_comp_propellants',
    // P221: 'formul_comp_chem_react_reg',
    // P222: 'formul_comp_corrosion_inhibitor',
    // P223: 'formul_comp_soil_amendments',
    // P224: 'formul_comp_sealant',
    // P225: 'formul_comp_explosion_inhibitor',
    // P226: 'formul_comp_fire_extinguishing',
    // P227: 'formul_comp_flavoring_nutrient',
    // P228: 'formul_comp_embalming',
    // P229: 'formul_comp_preservative',
    P299: 'formul_comp_other',
  };
  for (const [category, key] of Object.entries(formulationMapping)) {
    if (epaIsTrueFlag(ez[key])) {
      formulationComponents.push(category as ChemicalActivitiesAndUses['process']['formulationComponents'][number]);
    }
  }

  const chemicalProcessingAid: ChemicalActivitiesAndUses['otherwiseUse']['chemicalProcessingAid'] = [];
  const chemicalProcessingAidMapping: Partial<
    Record<
      ChemicalActivitiesAndUses['otherwiseUse']['chemicalProcessingAid'][number],
      keyof EpaApiTriFormREZ<CasOrCategory>
    >
  > = {
    Z101: 'chem_process_aid_process_solvs',
    Z102: 'chem_process_aid_catalysts',
    Z103: 'chem_process_aid_inhibitors',
    Z104: 'chem_process_aid_initiators',
    Z105: 'chem_process_aid_react_termin',
    Z106: 'chem_process_aid_solution_buf',
    // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
    // Z107: 'chem_process_aid_corrosion_inhibitor',
    // Z108: 'chem_process_aid_absorbent',
    // Z109: 'chem_process_aid_adsorbent',
    // Z110: 'chem_process_aid_semiconductor',
    // Z111: 'chem_process_aid_explosion_inhibitor',
    // Z112: 'chem_process_aid_tracer',
    Z199: 'chem_process_aid_other',
  };
  for (const [category, key] of Object.entries(chemicalProcessingAidMapping)) {
    if (epaIsTrueFlag(ez[key])) {
      chemicalProcessingAid.push(
        category as ChemicalActivitiesAndUses['otherwiseUse']['chemicalProcessingAid'][number],
      );
    }
  }

  const manufacturingAid: ChemicalActivitiesAndUses['otherwiseUse']['manufacturingAid'] = [];
  const manufacturingAidMapping: Partial<
    Record<ChemicalActivitiesAndUses['otherwiseUse']['manufacturingAid'][number], keyof EpaApiTriFormREZ<CasOrCategory>>
  > = {
    Z201: 'manuf_aid_process_lubricants',
    Z202: 'manuf_aid_metalworking_fluids',
    Z203: 'manuf_aid_coolants',
    Z204: 'manuf_aid_refrigerants',
    Z205: 'manuf_aid_hydraulic_fluids',
    // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
    // Z206: 'manuf_aid_corrosion_inhibitor',
    // Z207: 'manuf_aid_heat_transfer',
    // Z208: 'manuf_aid_refrigerants_z208',
    // Z209: 'manuf_aid_plating',
    Z299: 'manuf_aid_other',
  };
  for (const [category, key] of Object.entries(manufacturingAidMapping)) {
    if (epaIsTrueFlag(ez[key])) {
      manufacturingAid.push(category as ChemicalActivitiesAndUses['otherwiseUse']['manufacturingAid'][number]);
    }
  }

  const ancillaryOrOtherUse: ChemicalActivitiesAndUses['otherwiseUse']['ancillaryOrOtherUse'] = [];
  const ancillaryOrOtherUseMapping: Partial<
    Record<
      ChemicalActivitiesAndUses['otherwiseUse']['ancillaryOrOtherUse'][number],
      keyof EpaApiTriFormREZ<CasOrCategory>
    >
  > = {
    Z301: 'ancl_other_use_cleaner',
    Z302: 'ancl_other_use_degreaser',
    Z303: 'ancl_other_use_lubricant',
    Z304: 'ancl_other_use_fuel',
    Z305: 'ancl_other_use_flame_retardant',
    Z306: 'ancl_other_use_waste_treat',
    Z307: 'ancl_other_use_water_treat',
    Z308: 'ancl_other_use_construct_matls',
    // These are guesses that will need to be updated once TRI 2025 has been processed and updates published by the EPA:
    // Z309: 'ancl_other_use_deodorizer',
    // Z310: 'ancl_other_use_fragrance',
    // Z311: 'ancl_other_use_abrasives',
    // Z312: 'ancl_other_use_binder',
    // Z313: 'ancl_other_use_sealant',
    // Z314: 'ancl_other_use_energy_releasers',
    // Z315: 'ancl_other_use_etching',
    // Z316: 'ancl_other_use_fire_extinguishing',
    // Z317: 'ancl_other_use_embalming',
    Z399: 'ancl_other_use_other',
  };
  for (const [category, key] of Object.entries(ancillaryOrOtherUseMapping)) {
    if (epaIsTrueFlag(ez[key])) {
      ancillaryOrOtherUse.push(category as ChemicalActivitiesAndUses['otherwiseUse']['ancillaryOrOtherUse'][number]);
    }
  }

  return {
    manufacture: {
      produce: epaIsTrueFlag(ez.produce),
      import: epaIsTrueFlag(ez.imported),
      onSiteUseOrProcessing: epaIsTrueFlag(ez.used_processed),
      saleOrDistribution: epaIsTrueFlag(ez.sale_distribution),
      byProduct: epaIsTrueFlag(ez.byproduct),
      impurity: epaIsTrueFlag(ez.manufacture_impurity),
    },
    process: {
      reactantCategories,
      formulationComponents,
      articleComponent: epaIsTrueFlag(ez.article_component),
      repackaging: epaIsTrueFlag(ez.repackaging),
      impurity: epaIsTrueFlag(ez.process_impurity),
      recycling: epaIsTrueFlag(ez.processed_recycling),
    },
    otherwiseUse: {
      chemicalProcessingAid,
      manufacturingAid,
      ancillaryOrOtherUse,
    },
  };
}
